import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { FileSpreadsheet, Link2 } from 'lucide-react'
import { announcementsApi, type MemberDetail, type MemberImportResult, type MemberSummary } from '../../lib/infoserviceApi.js'
import { MEMBER_CATEGORIES, frenchDate, quotaForCategory } from '../../lib/memberQuota.js'
import { isDayKey } from '../../lib/announcementGrid.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import { MemberDrawer } from './MemberDrawer.js'
import { MemberImportModal } from './MemberImportModal.js'
import { QuotaGauge } from './QuotaGauge.js'
import { SpellingsModal } from './SpellingsModal.js'
import './InfoserviceMembers.css'

interface MembersPanelProps {
  channelId: string | null
  members: MemberSummary[]
  loading: boolean
  canManage: boolean
  /** Jour de diffusion `AAAA-MM-JJ`, pour l'année en cours de la frise. */
  today: string
  onChanged: () => void
  /** Ouvre la fiche de l'annonce dans l'onglet Annonces. */
  onOpenAnnouncement: (id: string) => void
}

/**
 * Onglet Adhérents : un adhérent par ligne (repris du fichier du club), sa
 * jauge de semaines sur l'année d'adhésion, ses annonces. Même densité aérée
 * que le tableau des annonces : une ligne de 36 px, des marques plutôt qu'un
 * quadrillage. Rien ici ne touche le calendrier des annonces.
 */
export const MembersPanel = memo(function MembersPanel({ channelId, members, loading, canManage, today, onChanged, onOpenAnnouncement }: MembersPanelProps) {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dialog, setDialog] = useState<'create' | 'import' | 'spellings' | null>(null)
  const [unresolved, setUnresolved] = useState<number | null>(null)
  const [notice, setNotice] = useState('')

  const refreshUnresolved = useCallback(() => {
    if (!channelId) return
    announcementsApi.members.spellings(channelId)
      .then(list => setUnresolved(list.filter(spelling => spelling.status === 'UNRESOLVED').length))
      .catch(() => setUnresolved(null))
  }, [channelId])
  useEffect(() => { refreshUnresolved() }, [refreshUnresolved, members])

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('fr')
    return needle ? members.filter(member => member.name.toLocaleLowerCase('fr').includes(needle)) : members
  }, [members, search])
  const full = members.filter(member => member.period && member.weeksUsed >= member.quota).length
  const expired = members.filter(member => member.expired).length
  const undated = members.filter(member => !member.period).length

  const imported = useCallback((result: MemberImportResult) => {
    setDialog(null)
    setNotice(`${result.created} adhérent${result.created > 1 ? 's' : ''} ajouté${result.created > 1 ? 's' : ''}, ${result.updated} mis à jour. `
      + `${result.attached} annonce${result.attached > 1 ? 's' : ''} rattachée${result.attached > 1 ? 's' : ''} ; ${result.unresolved} écriture${result.unresolved > 1 ? 's' : ''} de la feuille à rattacher.`)
    onChanged()
  }, [onChanged])

  return (
    <section id="infoservice-tab-panel" role="tabpanel" aria-labelledby="infoservice-tab-members" className="announcements-workspace">
      <div className="announcements-summary">
        <div className="announcements-summary__title">
          <h2 className="announcements-summary__heading">Adhérents</h2>
          <p className="announcements-summary__note">
            Chaque adhérent a droit à un nombre de semaines d’annonces par année d’adhésion. Une semaine compte dès qu’une de ses annonces y est cochée.
          </p>
        </div>
        <div className="announcements-summary__metric">
          <span className="announcements-summary__label">Adhérents</span>
          <span className="announcements-summary__value">{members.length}</span>
          <span className="announcements-summary__label">{expired} échu{expired > 1 ? 's' : ''} · {undated} sans date</span>
        </div>
        <div className="announcements-summary__metric">
          <span className="announcements-summary__label">Droit atteint</span>
          <span className="announcements-summary__value">{full}</span>
          <span className="announcements-summary__label">toutes semaines prises</span>
        </div>
        <div className="announcements-summary__tools">
          <input className="tp-input" type="search" placeholder="Nom de l’adhérent…" aria-label="Rechercher un adhérent" value={search} onChange={event => setSearch(event.target.value)} />
          <button type="button" className="tp-button" disabled={!channelId} onClick={() => setDialog('spellings')}>
            <Link2 size={14} aria-hidden="true" /> À rattacher{unresolved !== null && <span className="members-panel__badge">{unresolved}</span>}
          </button>
          <button type="button" className="tp-button" disabled={!channelId || !canManage} onClick={() => setDialog('import')}
            title={!canManage ? 'Permission de gestion publicitaire requise' : undefined}>
            <FileSpreadsheet size={14} aria-hidden="true" /> Importer le fichier du club
          </button>
          <button type="button" className="tp-button tp-button--primary" disabled={!channelId || !canManage} onClick={() => setDialog('create')}
            title={!canManage ? 'Permission de gestion publicitaire requise' : undefined}>
            + Nouvel adhérent
          </button>
        </div>
      </div>

      {notice && (
        <div className="tp-feedback tp-feedback--success members-panel__notice" role="status">
          {notice}
          <button type="button" className="tp-icon-button" aria-label="Masquer" onClick={() => setNotice('')}>×</button>
        </div>
      )}

      {loading && members.length === 0 ? (
        <div className="tp-empty-state announcements-state" aria-live="polite"><Spinner /> Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="tp-empty-state announcements-state">
          {search ? 'Aucun adhérent ne correspond à la recherche.' : 'Aucun adhérent. Importez le fichier du club, ou créez-les ici.'}
        </div>
      ) : (
        <div className="announcements-table-wrap">
          <div className="members-table__scroll">
            <table className="members-table">
              <thead>
                <tr>
                  <th scope="col">Adhérent</th>
                  <th scope="col">Catégorie</th>
                  <th scope="col">Adhésion</th>
                  <th scope="col">Semaines</th>
                  <th scope="col" className="members-table__number">Annonces</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(member => (
                  <MemberRow key={member.id} member={member} selected={member.id === selectedId} onSelect={setSelectedId} />
                ))}
              </tbody>
            </table>
          </div>
          <footer className="announcements-legend" aria-label="Légende">
            <span><span className="quota-gauge__slot is-passed" aria-hidden="true" /> Semaine passée</span>
            <span><span className="quota-gauge__slot is-planned" aria-hidden="true" /> Semaine prévue</span>
            <span><span className="quota-gauge__slot is-passed is-over" aria-hidden="true" /> Au-delà du droit annuel</span>
            <span><span className="quota-gauge__slot is-passed is-unknown" aria-hidden="true" /> Date d’adhésion à renseigner</span>
            <span><span className="quota-gauge__bar is-legend" aria-hidden="true"><span className="quota-gauge__bar-passed" /></span> Au-delà de 12 semaines par an (entreprises)</span>
          </footer>
        </div>
      )}

      {selectedId && (
        <MemberDrawer
          key={selectedId}
          id={selectedId}
          members={members}
          canManage={canManage}
          currentYear={Number(today.slice(0, 4))}
          onClose={() => setSelectedId(null)}
          onChanged={onChanged}
          onDeleted={() => { setSelectedId(null); onChanged() }}
          onMerged={intoId => { setSelectedId(intoId); onChanged() }}
          onOpenAnnouncement={onOpenAnnouncement}
        />
      )}
      {dialog === 'create' && channelId && (
        <NewMemberModal
          channelId={channelId}
          onClose={() => setDialog(null)}
          onCreated={created => { setDialog(null); setSelectedId(created.id); onChanged() }}
        />
      )}
      {dialog === 'import' && channelId && (
        <MemberImportModal channelId={channelId} onClose={() => setDialog(null)} onImported={imported} />
      )}
      {dialog === 'spellings' && channelId && (
        <SpellingsModal channelId={channelId} members={members} canManage={canManage} onClose={() => setDialog(null)} onChanged={onChanged} />
      )}
    </section>
  )
})

const MemberRow = memo(function MemberRow({ member, selected, onSelect }: { member: MemberSummary; selected: boolean; onSelect: (id: string) => void }) {
  return (
    <tr className={`members-table__row${selected ? ' is-selected' : ''}`}>
      <th scope="row">
        <button type="button" className="members-table__open" onClick={() => onSelect(member.id)} aria-pressed={selected}>
          {member.name}
        </button>
      </th>
      <td className="members-table__category" title={member.category ? MEMBER_CATEGORIES[member.category] : 'Sans catégorie'}>
        {member.category ? `C${member.category}` : '—'}
      </td>
      <td className="members-table__date">
        {member.period
          ? <span className={member.expired ? 'members-table__expired' : undefined}>{member.expired ? 'échue le ' : 'jusqu’au '}{frenchDate(member.period.end)}</span>
          : <span className="members-table__missing">à renseigner</span>}
      </td>
      <td><QuotaGauge member={member} /></td>
      <td className="members-table__number">{member.announcementCount}</td>
    </tr>
  )
})

const NewMemberModal = memo(function NewMemberModal({ channelId, onClose, onCreated }: { channelId: string; onClose: () => void; onCreated: (member: MemberDetail) => void }) {
  const [name, setName] = useState('')
  const [membershipDate, setMembershipDate] = useState('')
  const [category, setCategory] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim()) {
      setError('Le nom est obligatoire.')
      return
    }
    if (membershipDate && !isDayKey(membershipDate)) {
      setError('La date d’adhésion est incomplète.')
      return
    }
    setBusy(true)
    setError('')
    const categoryValue = category ? Number(category) : null
    try {
      onCreated(await announcementsApi.members.create({
        channelId,
        name: name.trim(),
        membershipDate: membershipDate || null,
        category: categoryValue,
        weekQuota: quotaForCategory(categoryValue),
      }))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Création impossible')
      setBusy(false)
    }
  }, [category, channelId, membershipDate, name, onCreated])

  return (
    <ModalShell title="Nouvel adhérent" onClose={onClose} closeDisabled={busy}>
      <form className="tp-modal-form tp-modal__body" onSubmit={event => void submit(event)}>
        <label className="tp-form-field">
          <span className="tp-field-label">Nom (celui du fichier du club)</span>
          <input className="tp-input" required autoFocus value={name} placeholder="LA COURTE ECHELLE" onChange={event => setName(event.target.value)} />
        </label>
        <div className="tp-form-grid">
          <label className="tp-form-field">
            <span className="tp-field-label">Catégorie</span>
            <select className="tp-input" value={category} onChange={event => setCategory(event.target.value)}>
              <option value="">Sans catégorie</option>
              {Object.entries(MEMBER_CATEGORIES).map(([value, label]) => <option key={value} value={value}>C{value} · {label}</option>)}
            </select>
          </label>
          <label className="tp-form-field">
            <span className="tp-field-label">Adhésion ou dernier renouvellement</span>
            <input className="tp-input" type="date" value={membershipDate} onChange={event => setMembershipDate(event.target.value)} />
          </label>
        </div>
        <span className="announcement-drawer__hint">
          Droit annuel : {quotaForCategory(category ? Number(category) : null)} semaines, modifiable dans la fiche.
          Les annonces dont l’organisme porte ce nom lui seront rattachées.
        </span>
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}
        <div className="tp-modal-actions">
          <button type="button" className="tp-button" onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className="tp-button tp-button--primary" disabled={busy}>{busy && <Spinner />} Créer l’adhérent</button>
        </div>
      </form>
    </ModalShell>
  )
})
